import * as React from 'react'
import PropTypes from 'prop-types'

// --- Lib ---
import { parseShortcutKeys } from '@tiptap-ui/lib/tiptap-utils'

// --- Hooks ---
import { useTiptapEditor } from '@tiptap-ui/hooks/use-tiptap-editor'

import { Button } from '@tiptap-ui/components/tiptap-ui-primitive/button'
import { Badge } from '@tiptap-ui/components/tiptap-ui-primitive/badge'

import { LIST_SHORTCUT_KEYS, useList } from '@tiptap-ui/components/tiptap-ui/list-button'

export function ListShortcutBadge ({
  type,
  shortcutKeys = LIST_SHORTCUT_KEYS[type],
}) {
  return <Badge>{parseShortcutKeys({ shortcutKeys })}</Badge>
}

ListShortcutBadge.propTypes = {
  type: PropTypes.string.isRequired,
  shortcutKeys: PropTypes.string,
}

/**
 * Button component for toggling lists in a Tiptap editor.
 *
 * For custom button implementations, use the `useList` hook instead.
 */
export const ListButton = React.forwardRef((
  {
    editor: providedEditor,
    type,
    text,
    hideWhenUnavailable = false,
    onToggled,
    showShortcut = false,
    onClick,
    children,
    ...buttonProps
  },
  ref
) => {
  const { editor } = useTiptapEditor(providedEditor)
  const {
    isVisible,
    canToggle,
    isActive,
    handleToggle,
    label,
    shortcutKeys,
    Icon,
  } = useList({
    editor,
    type,
    hideWhenUnavailable,
    onToggled,
  })

  const handleClick = React.useCallback((event) => {
    onClick?.(event)
    if (event.defaultPrevented) return
    handleToggle()
  }, [handleToggle, onClick])

  if (!isVisible) {
    return null
  }

  return (
    <Button
      type='button'
      data-style='ghost'
      data-active-state={isActive ? 'on' : 'off'}
      role='button'
      tabIndex={-1}
      disabled={!canToggle}
      data-disabled={!canToggle}
      aria-label={label}
      aria-pressed={isActive}
      tooltip={label}
      onClick={handleClick}
      {...buttonProps}
      ref={ref}
    >
      {children ?? (
        <>
          <Icon className='tiptap-button-icon' />
          {text && <span className='tiptap-button-text'>{text}</span>}
          {showShortcut && (
            <ListShortcutBadge type={type} shortcutKeys={shortcutKeys} />
          )}
        </>
      )}
    </Button>
  )
})

ListButton.displayName = 'ListButton'
ListButton.propTypes = {
  editor: PropTypes.object,
  type: PropTypes.string.isRequired,
  text: PropTypes.string,
  hideWhenUnavailable: PropTypes.bool,
  onToggled: PropTypes.func,
  showShortcut: PropTypes.bool,
  onClick: PropTypes.func,
  children: PropTypes.node,
}
